import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { IPlan } from 'src/app/domain/registration/models/IPlan';
import { ISystemModule } from 'src/app/domain/registration/models/ISystemModule'; // Ajuste o caminho se necessário
import { SystemModuleService } from '../../system-modules/crud-service'; // Ajuste o caminho se necessário
import { SystemService } from '../../systems/crud-service';
import { PlanService } from '../crud-service';
import { IPlanForSelection, ISystemForSelection, ISystemModuleForSelection } from './plan-selection.models';

@Injectable()
export class PlanSelectionService {

    constructor(
        private httpClient: HttpClient,
        private planService: PlanService,
        private systemService: SystemService,
        private systemModuleService: SystemModuleService // Injeta o serviço de módulos
    ) { }

    /**
     * Busca planos disponíveis, aplicando filtros e mapeando para o modelo de seleção.
     */
    getAvailablePlans(filters: { systemType?: number; partnerIds?: string[] }): Observable<IPlanForSelection[]> {
        // Adapte os filtros conforme a API de planos aceita
        const apiFilters: any = {};
        if (filters.systemType) {
            apiFilters.type = filters.systemType;
        }
        if (filters.partnerIds && filters.partnerIds.length > 0) {
            apiFilters.partnerIds = filters.partnerIds;
        }

        // Modificado para solicitar a relação 'system' e aumentar o limite para 1000
        return this.planService.findByFilter(apiFilters, { limit: 1000, page: 0, getRelation: 'system,partners' })
            .pipe(
                map((response: any) => {
                    const plans: IPlan[] = response[0] || [];

                    let filteredPlans = plans.filter(plan => {
                        if (!filters.partnerIds || filters.partnerIds.length === 0) {
                            return true;
                        }

                        return (!plan.partners || plan.partners.length === 0) ||
                            (plan.partners && plan.partners.some(partner =>
                                partner.id && filters.partnerIds?.includes(partner.id)
                            ));
                    });

                    return filteredPlans.map(plan => this.mapPlanToSelectionModel(plan));
                })
            );
    }

    /**
     * Busca os detalhes do sistema principal associado a um plano.
     */
    getSystemForPlan(plan: IPlanForSelection): Observable<ISystemForSelection | null> {
        if (!plan.systemId) {
            return of(null); 
        }
        return this.systemService.getSystemWithTemplate(plan.systemId)
            .pipe(
                map(system => system ? this.mapSystemToSelectionModel(system) : null)
            );
    }

    /**
     * Busca módulos disponíveis que podem ser vinculados a um sistema específico.
     */
    getAvailableModules(systemId: string): Observable<ISystemModuleForSelection[]> {
        // Filtro para buscar apenas módulos vinculados ao systemId específico
        const filters = { system: systemId };

        return this.systemModuleService.findByFilter(filters, { limit: 100, page: 0 })
            .pipe(
                map((response: any) => {
                    const modules: ISystemModule[] = response[0] || [];

                    return modules.map(mod => this.mapModuleToSelectionModel(mod));
                })
            );
    }

    private mapPlanToSelectionModel(plan: any): IPlanForSelection {
        let mappedDueDate: number | null = null; 

        if (plan.system && plan.system.dueDate !== undefined && plan.system.dueDate !== null) {
            const numericDueDate = Number(plan.system.dueDate);
            if (!isNaN(numericDueDate)) {
                mappedDueDate = numericDueDate;
            } else {
                console.warn(`[PlanSelectionService] Plan ID ${plan.id}, System ID ${plan.system?.id} has invalid numeric system.dueDate: ${plan.system.dueDate}. Defaulting to null.`);
            }
        }

        const monthlyValue = typeof plan.monthlyValue === 'number' ? plan.monthlyValue :
            (plan.monthlyValue ? Number(plan.monthlyValue) : 0);

        const adhesionFee = typeof plan.adhesionFee === 'number' ? plan.adhesionFee :
            (plan.adhesionFee ? Number(plan.adhesionFee) : 0);

        const terminalQuantity = typeof plan.terminalQuantity === 'number' ? plan.terminalQuantity :
            (plan.terminalQuantity ? Number(plan.terminalQuantity) : 0);

        const systemName = plan.system?.name || 'Plano Sem Nome';

        return {
            id: plan.id,
            systemId: plan.system?.id,
            isDaysCount: plan.isDaysCount === true,
            systemName: systemName,
            image: plan.system?.image,
            terminalQuantity: terminalQuantity,
            isActive: plan.isActive !== undefined ? plan.isActive : true,
            type: plan.type, 
            monthlyValue: monthlyValue,
            adhesionFee: adhesionFee,
            dueDate: mappedDueDate,
        };
    }

    private mapSystemToSelectionModel(system: any): ISystemForSelection {
        return {
            id: system.id,
            name: system.name,
            type: system.type, 
            image: system.image, 
            isActive: system.status === 1 || system.status === undefined, 
        };
    }

    // Novo mapeador para módulos
    private mapModuleToSelectionModel(module: any): ISystemModuleForSelection {
        return {
            id: module.id,
            name: module.name,
            description: module.description, 
            isActive: module.isActive !== undefined ? module.isActive : true, 
        };
    }

} 
